import { useCallback, useEffect, useMemo, useState } from 'react';
import { Message } from '@progress/kendo-react-conversational-ui';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../common/store/slices/auth.slice';
import { useLocation } from 'react-router-dom';
import {
  summarizeMessage,
  coachMeMessage,
  getShowTypingMessage,
} from '../../common/components/widgets/widget-elsie-ai/constants';

import {
  elsieApi,
  useGetCoachMeMutation,
  useSendMessageMutation,
  useSendMessageV2Mutation,
  useSendSummarizeMutation,
  useSendSummarizeV2Mutation,
} from '../../common/store/api/elsie.api';
import { useModuleProject } from '../../common/hooks/module';
import {
  selectConversationId,
  setConversationId,
  setTotalTokens,
  resetElsieStore,
  toggleCitations,
  selectCitations,
  selectCitationsOrder,
  selectTargetCitation,
} from '../../common/store/slices/elsie-ai.slice';
import { Citation } from '../../common/models/elsie-ai.model';
import { convertToLocalTime } from 'common/components/widgets/widget-utils/LocalTimeConversion';
import { selectAppliedFilters } from 'common/store/slices/common-filters.slice';
import {
  DATASET_INITIAL_ID,
  ELSIE_AI_V2_CHAT_SHOW_BRANCH,
  ELSIE_AI_V2_CHAT_SHOW_QUERY,
} from 'common/constants';
import { RootState } from 'common/store';
import { selectEndDate, selectStartDate } from 'common/store/slices/date-filter.slice';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { AppTeam } from 'common/models/page-config.models';
import {
  getCitationsFromOrdinals,
  normalizeCitations,
  createCitationOrdinalsMap,
} from '../utils/citationUtils';
import { getAiUser } from '../utils/messageUtils';

interface UseElsieAI {
  kwelloIds: string[];
  version: ElsieVersion;
}

export type ElsieVersion = 1 | 2;

function useElsieAI({ kwelloIds, version }: UseElsieAI) {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<Message[]>([]);
  const conversationId = useSelector(selectConversationId);
  const user = useSelector(selectUser);
  const currentUser = useMemo(() => {
    return { id: user.email, name: user.externalName };
  }, [user]);
  const [sendMessage] = useSendMessageMutation();
  const [sendMessageV2] = useSendMessageV2Mutation();
  const [sendSummarize] = useSendSummarizeMutation();
  const [sendSummarizeV2] = useSendSummarizeV2Mutation();

  const [getCoachMe] = useGetCoachMeMutation();
  const { moduleUserData } = useModuleProject();
  const targetCitation = useSelector(selectTargetCitation);
  const citationIds = useSelector(selectCitations);
  const citationsOrder = useSelector(selectCitationsOrder);
  const citationsIdsSet = useMemo(() => {
    return new Set(targetCitation ? [targetCitation] : citationIds ?? []);
  }, [citationIds, targetCitation]);
  const location = useLocation();
  const currentPath = location.pathname;
  const [triggerGetAttachedFiles] = elsieApi.useLazyGetAttachedFilesQuery();

  const onDeleteAll = useCallback(() => {
    setMessages([]);
    dispatch(resetElsieStore());
  }, [dispatch]);

  useEffect(() => {
    onDeleteAll();
  }, [currentPath, onDeleteAll]);
  const reduxFilter = useSelector((state: RootState) =>
    selectAppliedFilters(state, DATASET_INITIAL_ID)
  );
  const reduxStartDate = useSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const reduxEndDate = useSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));
  const { activeProject } = useModuleProjectContext();
  const { teams } = activeProject ?? {};

  const teamIds = useMemo(() => teams?.map((team: AppTeam) => team.teamID) ?? [], [teams]);
  const filter = useMemo(
    () => ({
      ...reduxFilter,
      startDate: reduxStartDate,
      endDate: reduxEndDate,
    }),
    [reduxFilter, reduxStartDate, reduxEndDate]
  );
  // only for ElsieAI 1.0
  const onAddNewMessage = useCallback(
    async (question: string, conversationType: string = 'chatAdvanced'): Promise<void> => {
      if (!user.email) {
        return;
      }
      setMessages((state) => {
        const result = [...state];

        if (result.at(-1)?.typing) {
          result.splice(-1);
        }

        if (conversationType === 'summarize') {
          return [
            ...result,
            { ...summarizeMessage, author: currentUser },
            getShowTypingMessage(version),
          ];
        }
        return [...result, { text: question, author: currentUser }, getShowTypingMessage(version)];
      });

      const aiMethod = conversationType === 'summarize' ? sendSummarize : sendMessage;

      const messageResult: any = await aiMethod({
        ...moduleUserData,
        userId: moduleUserData.userId ?? '0',
        kwelloIds,
        conversationId,
        question,
      });

      const citationOrdinals = new Map();

      if (messageResult?.data) {
        dispatch(setConversationId({ conversationId: messageResult.data.conversationId }));
        dispatch(setTotalTokens({ totalTokens: messageResult.data.totalTokens }));

        Object.keys(messageResult.data.citationOrdinals).forEach((order) => {
          const orderIndex = parseInt(order.substring(1, order.lastIndexOf(']')), 10);
          if (!isNaN(orderIndex)) {
            citationOrdinals.set(messageResult.data.citationOrdinals[order], orderIndex);
          }
        });
      }

      setMessages((state) => {
        const result = [...state];
        const author = getAiUser(version);

        result.splice(-1);

        if (messageResult?.data) {
          let response = messageResult.data.chatResponse;

          if (conversationType === 'summarize' && messageResult.data.keyInsights?.length) {
            messageResult.data.keyInsights.forEach((insight: any, index: number) => {
              response = `${response}\n${index === 0 ? '\n' : ''}${insight}`;
            });
          }

          return [
            ...result,
            {
              text: response,
              author,
              meta: {
                citations: messageResult.data.citations,
                citationsOrder: citationOrdinals,
              },
            },
          ];
        }

        if ('error' in messageResult) {
          return [
            ...result,
            {
              author,
              status: messageResult.error?.originalStatus?.toString(),
              text: messageResult.error.data,
            },
          ];
        }

        return state;
      });
    },
    [
      conversationId,
      currentUser,
      dispatch,
      kwelloIds,
      moduleUserData,
      version,
      sendMessage,
      sendSummarize,
      user,
    ]
  );

  // only for ElsieAi 2.0
  const onAddNewMessageV2 = useCallback(
    async (
      question: string,
      conversationType: string = 'chatAdvanced',
      conversationIdElsieV2: string,
      dataSources: string[]
    ): Promise<void> => {
      if (!user.email) {
        return;
      }
      let apiFiles: any[] = [];
      if (dataSources.includes('upload_documents')) {
        try {
          if (!activeProject?.moduleProjectID) throw new Error('No projectId');
          const response = await triggerGetAttachedFiles({
            conversationId: conversationIdElsieV2,
            projectId: activeProject.moduleProjectID,
          }).unwrap();
          apiFiles = Array.isArray(response?.documents)
            ? response.documents
            : Array.isArray(response)
              ? response
              : [];
        } catch (err) {
          console.error('Failed to fetch attached files:', err);
        }
      } else {
        apiFiles = [];
      }
      setMessages((state) => {
        const result = [...state];

        if (result.at(-1)?.typing) {
          result.splice(-1);
        }

        if (conversationType === 'summarize') {
          return [
            ...result,
            { ...summarizeMessage, author: currentUser, timestamp: new Date(), files: apiFiles },
            getShowTypingMessage(version),
          ];
        }
        return [
          ...result,
          { text: question, author: currentUser, timestamp: new Date(), files: apiFiles },
          getShowTypingMessage(version),
        ];
      });

      const aiMethod = conversationType === 'summarize' ? sendSummarizeV2 : sendMessageV2;
      const messageResult: any = await aiMethod({
        ...moduleUserData,
        userId: moduleUserData.userId ?? '0',
        kwelloIds,
        conversationId: conversationIdElsieV2,
        question,
        filter,
        projectClientName: activeProject?.projectClientName,
        teamIds: teamIds,
        dataSources,
        showQuery: ELSIE_AI_V2_CHAT_SHOW_QUERY,
        showBranch: ELSIE_AI_V2_CHAT_SHOW_BRANCH,
      });

      const citationOrdinals = createCitationOrdinalsMap(messageResult?.data?.citationOrdinals);

      if (messageResult?.data) {
        dispatch(setConversationId({ conversationId: messageResult.data.conversationId }));
        dispatch(setTotalTokens({ totalTokens: messageResult.data.totalTokens }));
      }

      setMessages((state) => {
        const result = [...state];

        result.splice(-1);
        const formattedTime = convertToLocalTime(messageResult.data?.time);
        const responseTime = formattedTime?.timeOnly ? formattedTime?.timeOnly : '';
        const author = getAiUser(version, responseTime);

        const isError =
          messageResult?.data?.error ||
          messageResult?.Success === false ||
          messageResult?.StatusCode >= 400 ||
          'error' in messageResult;

        if (isError) {
          return [
            ...result,
            {
              author,
              timestamp: new Date(),
              text: "I can't answer your question at the moment, please try again later.",
            },
          ];
        }

        if (messageResult?.data) {
          let response = messageResult.data.chatResponse ?? '';
          response = normalizeCitations(response);
          if (conversationType === 'summarize' && messageResult.data.keyInsights?.length) {
            messageResult.data.keyInsights.forEach((insight: any, index: number) => {
              response = `${response}\n${index === 0 ? '\n' : ''}${normalizeCitations(insight)}`;
            });
          }

          const orderedCitations = getCitationsFromOrdinals({
            citationOrdinals,
            citations: messageResult.data.citations as unknown as Citation[],
          });
          const citationIds = orderedCitations.map((c) => c.id);
          return [
            ...result,
            {
              text: response,
              author,
              timestamp: new Date(),
              meta: {
                citations: citationIds, // string[]
                citations2: orderedCitations ?? [], // with the new citations from different sources
                citationsOrder: citationOrdinals, // Map<string, number>
              },
              table: messageResult?.data.table,
              visualization: messageResult?.data.visualization,
              time: messageResult?.data.time,
              highlights: messageResult?.data.highlights,
              followUp: messageResult?.data.followUp || [],
              dataSources: messageResult?.data?.dataSources ?? [],
            },
          ];
        }

        return state;
      });
    },
    [
      currentUser,
      dispatch,
      kwelloIds,
      moduleUserData,
      sendMessageV2,
      sendSummarizeV2,
      user,
      filter,
      activeProject?.projectClientName,
      teamIds,
      activeProject?.moduleProjectID,
      triggerGetAttachedFiles,
      version,
    ]
  );

  const onCoachMe = useCallback(async () => {
    if (!user.email) {
      return;
    }

    setMessages((state) => {
      const result = [...state];

      if (result.at(-1)?.typing) {
        result.splice(-1);
      }

      return [...result, { ...coachMeMessage, author: currentUser }, getShowTypingMessage(version)];
    });

    const coachMeResult: any = await getCoachMe({
      ...moduleUserData,
      userId: moduleUserData.userId ?? '0',
      conversationType: 'coachMe',
      kwelloIds,
      conversationId,
    });

    const citationOrdinals = new Map();
    const author = getAiUser(version);

    if (coachMeResult?.data) {
      dispatch(setConversationId({ conversationId: coachMeResult.data.conversationId }));
      dispatch(setTotalTokens({ totalTokens: coachMeResult.data.totalTokens }));
      Object.keys(coachMeResult?.data?.citationOrdinals).forEach((order) => {
        const orderIndex = parseInt(order.substring(1, order.lastIndexOf(']')), 10);
        if (!isNaN(orderIndex)) {
          citationOrdinals.set(coachMeResult?.data?.citationOrdinals[order], orderIndex);
        }
      });
    } else {
      dispatch(
        setConversationId({
          conversationId: 'a000a12a-0000-0000-aaaa-abc000000a0a',
        })
      );
    }

    setMessages((state) => {
      const result = [...state];

      result.splice(-1);

      if (coachMeResult?.data) {
        return [
          ...result,
          {
            text: coachMeResult?.data?.coachMe?.overallFeedback,
            author,
            meta: {
              citations: coachMeResult?.data?.citations,
              citationsOrder: citationOrdinals,
            },
          },
        ];
      }

      if (coachMeResult?.error) {
        return [
          ...result,
          {
            author,
            status: coachMeResult.error?.originalStatus?.toString(),
          },
        ];
      }

      return state;
    });
  }, [conversationId, currentUser, dispatch, getCoachMe, kwelloIds, moduleUserData, user, version]);

  const closeCitations = useCallback(() => {
    dispatch(toggleCitations({ isCitationsOpen: false }));
  }, [dispatch]);

  const getCitations = useCallback(
    ({ citationData }: { citationData?: Citation[] }) => {
      return citationData
        ?.reduce((acc: Citation[], item: Citation): Citation[] => {
          if (citationsIdsSet.has(`${item.responseId}`)) {
            acc.push({
              ...item,
              citationIndex: citationsOrder.get(`${item.responseId}`),
            });
          }
          return acc;
        }, [])
        .sort((a: Citation, b: Citation) => {
          return (a?.citationIndex ?? 0) - (b?.citationIndex ?? 0);
        });
    },
    [citationsIdsSet, citationsOrder]
  );

  return useMemo(
    () => ({
      messages,
      onAddNewMessage,
      onAddNewMessageV2,
      conversationId,
      onCoachMe,
      onDeleteAll,
      closeCitations,
      getCitations,
    }),
    [
      messages,
      onAddNewMessage,
      onAddNewMessageV2,
      conversationId,
      onCoachMe,
      onDeleteAll,
      closeCitations,
      getCitations,
    ]
  );
}

export default useElsieAI;
